import React from 'react';

import { IAppliedFilter } from 'components/Grids/DataGrid/Filters/utils/types';
import { IFilterSetting, INullable, ISortType, TSortProps } from 'utils/types';

export interface IEditingCell {
  dataKey: string;
  rowIndex: number;
}

export interface IGridContext {
  defaultSortedColumn?: string;
  disabled?: boolean;
  filters?: Record<string, IFilterSetting>;
  gridEditMode: boolean;
  isDefaultGridSorting?: boolean;
  onSort?: (config: TSortProps) => void;
  requestedSort?: IAppliedFilter;
  setGridEditMode: (flag: boolean) => void;
  sort?: ISortType;
}

export const DataGridContext = React.createContext<IGridContext>({
  gridEditMode: false,
  setGridEditMode: () => {},
});

export const useDataGridContext = () => React.useContext(DataGridContext);

interface IEditingCellContext {
  editingCell: INullable<IEditingCell>;
  setEditingCell: React.Dispatch<React.SetStateAction<INullable<IEditingCell>>>;
}

const EditingCellContext = React.createContext<IEditingCellContext>({
  editingCell: null,
  setEditingCell: () => {},
});

/*
  "Which cell is inline-editing" lives in its own provider (CMP-3791). The
  ui-kit virtualized grid re-creates its react-window cell renderer on every
  render, which remounts every visible cell whenever DataGrid re-renders — so
  this state must be able to change WITHOUT re-rendering DataGrid. Because the
  provider's children keep their element identity across its re-renders, React
  bails out of the grid subtree and only the context consumers (editable
  cells) re-render when the editing cell changes.
*/
export const EditingCellProvider = ({ children }: React.PropsWithChildren) => {
  const [editingCell, setEditingCell] = React.useState<INullable<IEditingCell>>(null);

  const { gridEditMode } = useDataGridContext();

  React.useEffect(() => {
    if (!gridEditMode) {
      setEditingCell(null);
    }
  }, [gridEditMode]);

  const value = React.useMemo(() => ({ editingCell, setEditingCell }), [editingCell]);

  return (
    <EditingCellContext.Provider value={value}>{children}</EditingCellContext.Provider>
  );
};

/*
  Single source of truth for a cell's inline-edit state. Opening cell B closes
  cell A in one render, and the state survives cell remounts caused by grid
  re-renders (data refetch after save, etc.).
*/
export const useCellEditMode = (dataKey: PropertyKey, rowIndex: number) => {
  const { gridEditMode } = useDataGridContext();
  const { editingCell, setEditingCell } = React.useContext(EditingCellContext);

  const cellDataKey = String(dataKey);

  const isEditMode =
    editingCell?.dataKey === cellDataKey && editingCell?.rowIndex === rowIndex;

  const openEditMode = React.useCallback(() => {
    setEditingCell({ dataKey: cellDataKey, rowIndex });
  }, [cellDataKey, rowIndex, setEditingCell]);

  // Functional update: only clear our own cell, so a late close event from a
  // previous cell can't discard the edit state of the newly opened one.
  const closeEditMode = React.useCallback(() => {
    setEditingCell((current) =>
      current?.dataKey === cellDataKey && current?.rowIndex === rowIndex ? null : current,
    );
  }, [cellDataKey, rowIndex, setEditingCell]);

  return { closeEditMode, gridEditMode, isEditMode, openEditMode };
};

export const DataGridProvider = DataGridContext.Provider;
